// src/lib/api.js
export const STRAPI_URL =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_STRAPI_URL) ||
  process.env.REACT_APP_STRAPI_URL ||
  process.env.NEXT_PUBLIC_STRAPI_URL ||
  "http://localhost:1337";

const API = STRAPI_URL;

/** --- Homepage (single type) ----------------------------------------------- */
const HOMEPAGE_UID = "home-page";

export async function getHomepage() {
  const url = `${API}/api/${HOMEPAGE_UID}?populate=*`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Homepage is empty or unpublished.");
  const attrs = data.attributes || data;

  const heroSection = {
    __component: "sections.hero",
    id: 1,
    heroTitle: attrs.heroTitle ?? "",
    heroPreamble: attrs.heroPreamble ?? "",
    heroTypingWords: Array.isArray(attrs.heroTypingWords)
      ? attrs.heroTypingWords
      : typeof attrs.heroTypingWords === "string"
        ? (() => { try { return JSON.parse(attrs.heroTypingWords); } catch { return []; } })()
        : [],
    heroImages: attrs.heroImages ?? [],
    teamPhoto: attrs.teamPhoto ? { url: mediaUrl(attrs.teamPhoto), alt: attrs.teamPhoto?.alternativeText ?? "Teamfoto" } : null,
    ctaPrimaryLabel: attrs.ctaPrimaryLabel ?? "",
    ctaPrimaryUrl: attrs.ctaPrimaryUrl ?? "",
    ctaSecondaryLabel: attrs.ctaSecondaryLabel ?? "",
    ctaSecondaryUrl: attrs.ctaSecondaryUrl ?? "",
    heroEyebrowLead: attrs.heroEyebrowLead ?? "",
    heroEyebrowMain: attrs.heroEyebrowMain ?? "",
  };

  return {
    sections: [heroSection],

    // "Was uns ausmacht" teaser (Schulprofil cards)
    featuresKicker: attrs.featuresKicker ?? "Rothenburgschule",
    featuresTitle: attrs.featuresTitle ?? "Was uns ausmacht",
    featuresButtonLabel: attrs.featuresButtonLabel ?? "Mehr erfahren",
    featuresButtonUrl: attrs.featuresButtonUrl ?? "/profil",

    // "Einblicke" teaser
    einblickeKicker: attrs.einblickeKicker ?? "Aktuelles",
    einblickeTitle: attrs.einblickeTitle ?? "Einblicke",
    einblickeButtonLabel: attrs.einblickeButtonLabel ?? "Alle ansehen",
    einblickeButtonUrl: attrs.einblickeButtonUrl ?? "/einblicke",

    // "Aktuelle Termine" teaser
    termineKicker: attrs.termineKicker ?? "Kalender",
    termineTitle: attrs.termineTitle ?? "Aktuelle Termine",
    termineButtonLabel: attrs.termineButtonLabel ?? "Alle Termine",
    termineButtonUrl: attrs.termineButtonUrl ?? "/termine",

    // "Unser Team" teaser
    teamKicker: attrs.teamKicker ?? "Menschen",
    teamTitle: attrs.teamTitle ?? "Unser Team",
    teamText: attrs.teamText ?? "Ein engagiertes Team, das unsere Schülerinnen und Schüler auf ihrem Lern und Lebensweg begleitet.",
    teamButtonLabel: attrs.teamButtonLabel ?? "Mehr über uns",
    teamButtonUrl: attrs.teamButtonUrl ?? "/team",
  };
}

/** --- Section helpers -------------------------------------------------------
 * Only the hero is still consumed as a "section" on the frontend (see
 * LandingPage.jsx). Kept here because normalizeHero()/isHeroSection() are
 * still in active use.
 */
export const uidOf = (s = {}) => String(s.__component || s.component || "");

const has = (uid, needle) => {
  const u = String(uid || "").toLowerCase();
  const n = String(needle || "").toLowerCase();
  return u === n || u.endsWith(n) || u.includes(n);
};

export const isHeroSection = (s = {}) => {
  const u = uidOf(s);
  return has(u, "hero") || has(u, "sections.hero") || has(u, "sections-hero") || has(u, "sections.sections-hero");
};

/** --- Normalize hero ------------------------------------------------------- */
export function normalizeHero(s = {}) {
  const pick = (obj, ...keys) => {
    for (const k of keys) if (obj[k] != null) return obj[k];
    return undefined;
  };

  const heroImages =
    s.images ?? s.heroImages ?? s.hero_images ?? s.gallery ?? s.heroGallery ?? s.hero_gallery ?? [];

  return {
    heroTitle: s.title ?? s.heroTitle ?? "",
    heroPreamble: s.preamble ?? s.heroPreamble ?? "",
    heroTypingWords: s.typingWords ?? s.typing_words ?? s.heroTypingWords ?? [],
    heroImages: Array.isArray(heroImages) ? heroImages : heroImages ? [heroImages] : [],
    ctaPrimaryLabel: pick(s, "primaryCtaLabel", "ctaPrimaryLabel", "cta_primary_label") ?? "",
    ctaPrimaryUrl: pick(s, "primaryCtaUrl", "ctaPrimaryUrl", "cta_primary_url") ?? "",
    ctaSecondaryLabel: pick(s, "secondaryCtaLabel", "ctaSecondaryLabel", "cta_secondary_label") ?? "",
    ctaSecondaryUrl: pick(s, "secondaryCtaUrl", "ctaSecondaryUrl", "cta_secondary_url") ?? "",
  };
}

/** --- Posts teaser helpers (generic fallback across possible content types) */
export async function getLatestPosts({ limit = 4 } = {}) {
  const candidates = ["posts", "post", "articles", "news", "blog-posts", "blog"];
  for (const ct of candidates) {
    try {
      const url = `${API}/api/${ct}?populate=*&sort=publishedAt:desc&pagination[pageSize]=${limit}`;
      const res = await fetch(url);
      if (!res.ok) continue;
      const json = await res.json();
      const items = (json?.data || []).map(normalizePost).filter(Boolean);
      if (items.length) return items;
    } catch { /* try next */ }
  }
  return [];
}

function normalizePost(item) {
  if (!item) return null;
  const a = item.attributes || item;
  const cover = a.cover || a.image || a.heroImage || a.thumbnail || null;
  const slug = a.slug || a.path || a.uid || String(item.id);
  const url = a.url || `/${slug}`;
  return {
    id: item.id ?? slug,
    title: a.title ?? "",
    excerpt: a.excerpt ?? a.summary ?? a.description ?? "",
    coverImage: cover,
    publishedAt: a.publishedAt ?? a.createdAt ?? null,
    url,
  };
}

/** --- Legal page (single type: Impressum + Datenschutz) -------------------- */
const LEGAL_PAGE_UID = "legal-page";

export async function getLegalPage() {
  const url = `${API}/api/${LEGAL_PAGE_UID}?populate=*`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const attrs = json?.data?.attributes || json?.data || {};
  return {
    impressumTitle: attrs.impressumTitle ?? "",
    impressumContent: attrs.impressumContent ?? [],
    datenschutzTitle: attrs.datenschutzTitle ?? "",
    datenschutzContent: attrs.datenschutzContent ?? [],
    cookieTitle: attrs.cookieTitle ?? "",
    cookieContent: attrs.cookieContent ?? [],
  };
}
const PARTNER_UID = "partners";

export async function getPartners() {
  const url = `${API}/api/${PARTNER_UID}?populate=*&sort=order:asc&pagination[pageSize]=100`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  return (json?.data || []).map((item) => {
    const a = item.attributes || item;
    return {
      id: item.id,
      name: a.name ?? "",
      label: a.label ?? "",
      intro: a.intro ?? "",
      points: typeof a.points === "string"
        ? a.points.split("\n").map((p) => p.trim()).filter(Boolean)
        : Array.isArray(a.points)
          ? a.points
          : [],
      website: a.website ?? "",
      order: a.order ?? 0,
      logo: a.logo ? { url: mediaUrl(a.logo), alt: a.logo?.alternativeText } : null,
    };
  });
}
/** --- Media URL helper ---------------------------------------------------- */
export function mediaUrl(file) {
  const url = file?.url || file?.data?.attributes?.url || null;
  if (!url) return null;
  return url.startsWith("http") ? url : `${API}${url}`;
}

/** --- Partner page (single type) ------------------------------------------- */
const PARTNER_PAGE_UID = "partner-page";

export async function getPartnerPage() {
  const url = `${API}/api/${PARTNER_PAGE_UID}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) return null;
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
  };
}
/** --- Schulprofil (single type) ------------------------------------------- */
const SCHULPROFIL_UID = "schulprofil";

export async function getSchulprofil() {
  const url =
  `${API}/api/${SCHULPROFIL_UID}` +
  `?populate[editorialBlocks][populate]=*` +
  `&populate[facts]=*`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();
  const data = json?.data;
  if (!data) throw new Error("Schulprofil is empty or unpublished.");
  const attrs = data.attributes || data;
  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
    factsTitle: attrs.factsTitle ?? "",
    factsSubtitle: attrs.factsSubtitle ?? "",
    editorialBlocks: (attrs.editorialBlocks || []).map((b) => ({
      id: b.id,
      kicker: b.kicker ?? "",
      title: b.title ?? "",
      text: b.text ?? "",
      icon: b.icon?.name ?? b.icon ?? "star",
      image: b.image ? { url: mediaUrl(b.image), alt: b.image?.alternativeText } : null,
    })),
    facts: (attrs.facts || []).map((f) => ({
      id: f.id,
      title: f.title ?? "",
      text: f.text ?? "",
    })),
  };
}

/** --- Team page (single type) + Teachers (collection) ---------------------- */
const TEAM_PAGE_UID = "team-page";

export async function getTeamPage() {
  const url = `${API}/api/${TEAM_PAGE_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Team page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
    terminTitle: attrs.terminTitle ?? "",
    terminText: attrs.terminText ?? "",
    terminPhone: attrs.terminPhone ?? "",
    terminEmail: attrs.terminEmail ?? "",
  };
}

const TEACHER_UID = "teachers";

export async function getTeachers() {
  // pagination[pageSize] raised to avoid Strapi's default 25-item cap
  const url = `${API}/api/${TEACHER_UID}?populate=*&sort=order:asc&pagination[pageSize]=100`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  return (json?.data || []).map((item) => {
    const a = item.attributes || item;
    return {
      id: item.id,
      name: a.name ?? "",
      role: a.role ?? "",
      category: a.category ?? "",
      phoneNumber: a.phoneNumber ?? "",
      subjects: typeof a.subjects === "string"
        ? a.subjects.split(",").map((s) => s.trim()).filter(Boolean)
        : Array.isArray(a.subjects)
          ? a.subjects
          : [],
      email: a.email ?? "",
      order: a.order ?? 0,
      text: a.text ?? "",
      photo: a.photo ? { url: mediaUrl(a.photo), alt: a.photo?.alternativeText } : null,
      drawing: a.drawing ? { url: mediaUrl(a.drawing), alt: a.drawing?.alternativeText } : null,
    };
  });
}

/** --- Räume page (single type) ---------------------------------------------
 * Single types in this project use the singular form in the route.
 */
const RAEUME_UID = "raeume-page";

export async function getRaeumePage() {
  const url =
    `${API}/api/${RAEUME_UID}` +
    `?populate[heroImage]=true` +
    `&populate[rooms][populate][image]=true`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Räume page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImage: attrs.heroImage
      ? { url: mediaUrl(attrs.heroImage), alt: attrs.heroImage?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
    rooms: (attrs.rooms || []).map((r) => ({
      id: r.id,
      title: r.title ?? "",
      text: r.text ?? "",
      image: r.image ? { url: mediaUrl(r.image), alt: r.image?.alternativeText } : null,
      tags: typeof r.tags === "string"
        ? r.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : Array.isArray(r.tags)
          ? r.tags
          : [],
    })),
  };
}

/** --- Global (single type) -------------------------------------------------
 * Site-wide settings: school name, address, email, phone.
 * Used across multiple pages (Anfahrt, footer, contact strips, etc.)
 */
export async function getSiteDesign() {
  const url = `${API}/api/site-design`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const a = json?.data?.attributes || json?.data || {};
  const px = (v, fallback) => `${v ?? fallback}px`;

  return {
    accentColor: a.accentColor || "#925a64",
    secondaryColor: a.secondaryColor || "#4f5574",
    textColor: a.textColor || "#101114",
    borderColor: a.borderColor || "#101114",
    backgroundColor: a.backgroundColor || "#f7f4f2",

    headerPadTop: px(a.headerPadTop, 40),
    headerPadBottom: px(a.headerPadBottom, 0),
    headerPadTopSm: px(a.headerPadTopSm, 34),
    headerPadBottomSm: px(a.headerPadBottomSm, 14),

    // Rhythm — fallbacks match tokens.css. Only these four drive the
    // spacing now: introPadTop(+Sm) = header → first section,
    // sectionPadBottom(+Sm) = between sections.
    introPadTop: px(a.introPadTop, 48),
    introPadBottom: px(a.introPadBottom, 72),      // unused
    introPadTopSm: px(a.introPadTopSm, 32),
    introPadBottomSm: px(a.introPadBottomSm, 58),  // unused

    sectionPadTop: px(a.sectionPadTop, 72),        // unused
    sectionPadBottom: px(a.sectionPadBottom, 96),
    sectionPadTopSm: px(a.sectionPadTopSm, 58),    // unused
    sectionPadBottomSm: px(a.sectionPadBottomSm, 64),

    gridGap: px(a.gridGap, 16),
  };
}

export async function getGlobal() {
  const url = `${API}/api/global`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const attrs = json?.data?.attributes || json?.data || {};

  const parseLinks = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string") { try { return JSON.parse(v); } catch { return []; } }
    return [];
  };

  return {
    siteName:        attrs.siteName ?? "",
    schoolName:      attrs.schoolName ?? "",
    schoolAddress:   attrs.schoolAddress ?? "",
    schoolEmail:     attrs.schoolEmail ?? "",
    schoolPhone:     attrs.schoolPhone ?? "",
    footerHours:     attrs.footerHours ?? "",
    footerTagline:   attrs.footerTagline ?? "",
    footerMapUrl:    attrs.footerMapUrl ?? "",
    footerCol1Title: attrs.footerCol1Title ?? "Kontakt",
    footerCol2Title: attrs.footerCol2Title ?? "Schule",
    footerCol3Title: attrs.footerCol3Title ?? "Service",
    footerLinks1:    parseLinks(attrs.footerLinks1),
    footerLinks2:    parseLinks(attrs.footerLinks2),
    footerLinks3:    parseLinks(attrs.footerLinks3),
    imprintUrl:      attrs.imprintUrl ?? "/impressum",
    privacyUrl:      attrs.privacyUrl ?? "/datenschutz",
  };
}

/** --- Anfahrt page (single type) ------------------------------------------- */
const ANFAHRT_UID = "anfahrt-page";

export async function getAnfahrtPage() {
  const url = `${API}/api/${ANFAHRT_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Anfahrt page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    kicker: attrs.kicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
  };
}

/** --- Unterricht / Lernen & Verstehen page (single type) ------------------- */
const UNTERRICHT_UID = "lernen-verstehen-page";

export async function getUnterrichtPage() {
  const url =
    `${API}/api/${UNTERRICHT_UID}` +
    `?populate[heroImage]=true` +
    `&populate[miniCards][populate]=*` +
    `&populate[tabs][populate]=*` +
    `&populate[weekGrid][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Unterricht page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImage: attrs.heroImage
      ? { url: mediaUrl(attrs.heroImage), alt: attrs.heroImage?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    tabsSectionTitle: attrs.tabsSectionTitle ?? "",
    tabsSectionSubtitle: attrs.tabsSectionSubtitle ?? "",
    weekSectionTitle: attrs.weekSectionTitle ?? "",
    weekSectionSubtitle: attrs.weekSectionSubtitle ?? "",
    miniCards: (attrs.miniCards || []).map((c) => ({
      id: c.id,
      iconName: c.icon?.name ?? "star",
      title: c.title ?? "",
      text: c.text ?? "",
    })),
    tabs: (attrs.tabs || []).map((t) => ({
      id: t.id,
      key: String(t.id),
      label: t.label ?? "",
      kicker: t.kicker ?? "",
      title: t.title ?? "",
      text: t.text ?? "",
      bullets: typeof t.bullets === "string"
        ? t.bullets.split("\n").map((b) => b.trim()).filter(Boolean)
        : [],
      iconName: t.icon?.name ?? "star",
      image: t.image ? { url: mediaUrl(t.image), alt: t.image?.alternativeText } : null,
    })),
    weekGrid: (attrs.weekGrid || []).map((w) => ({
      id: w.id,
      iconName: w.icon?.name ?? "star",
      title: w.title ?? "",
      // Strapi field may be named "note" or "text" — check both
      note: w.note ?? w.text ?? "",
    })),
  };
}

/** --- Sprachen page (single type) ------------------------------------------ */
const SPRACHEN_UID = "sprachen-page";

export async function getSprachenPage() {
  const url =
    `${API}/api/${SPRACHEN_UID}` +
    `?populate[heroImage]=true` +
    `&populate[cards][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Sprachen page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImage: attrs.heroImage
      ? { url: mediaUrl(attrs.heroImage), alt: attrs.heroImage?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
    cards: (attrs.cards || []).map((c) => ({
      id: c.id,
      iconName: c.icon?.name ?? "star",
      title: c.title ?? "",
      text: c.text ?? "",
    })),
  };
}

/** --- Welt als Lernraum page (single type) ---------------------------------- */
const WELT_UID = "welt-page";

export async function getWeltPage() {
  const url =
    `${API}/api/${WELT_UID}` +
    `?populate[heroImages]=true` +
    `&populate[cards][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Welt page is empty or unpublished.");
  const attrs = data.attributes || data;

  const heroImagesRaw = Array.isArray(attrs.heroImages)
    ? attrs.heroImages
    : attrs.heroImages?.data || [];

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImages: heroImagesRaw
      .map((img) => {
        const a = img.attributes || img;
        return { url: mediaUrl(a), alt: a.alternativeText };
      })
      .filter((img) => img.url),
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText1: attrs.introText1 ?? "",
    introText2: attrs.introText2 ?? "",
    cards: (attrs.cards || []).map((c) => ({
      id: c.id,
      iconName: c.icon?.name ?? "star",
      title: c.title ?? "",
      text: c.text ?? "",
    })),
  };
}

/** --- Nach der Schule page (single type) ------------------------------------ */
const NACH_DER_SCHULE_UID = "nach-der-schule-page";

export async function getNachDerSchulePage() {
  const url =
    `${API}/api/${NACH_DER_SCHULE_UID}` +
    `?populate[heroImage]=true` +
    `&populate[kurse][populate][image]=true` +
    `&populate[kurse][populate][icon]=true` +
    `&populate[horte]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Nach der Schule page is empty or unpublished.");
  const attrs = data.attributes || data;

  // "horte" kommt je nach Strapi-Modellierung entweder als flache
  // Komponentenliste ([{...}]) oder als Relation ({ data: [{ id, attributes }] }).
  // Beide Formen hier abfangen, sonst verschwinden Felder wie "address" einfach.
  const horteList = attrs.horte?.data ?? attrs.horte ?? [];
  const kurseList = attrs.kurse?.data ?? attrs.kurse ?? [];

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImage: attrs.heroImage
      ? { url: mediaUrl(attrs.heroImage), alt: attrs.heroImage?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",

      kurseTitle: attrs.kurseTitle ?? "",
    kurseSubtitle: attrs.kurseSubtitle ?? "",
    kurse: kurseList.map((k) => {
      const ka = k.attributes || k;
      return {
        id: k.id,
        title: ka.title ?? "",
        text: ka.text ?? "",
        // Strapi-Feld heißt "link" (siehe Content-Type) — href/url nur als Fallback
        link: ka.link ?? ka.href ?? ka.url ?? "",
        image: ka.image ? { url: mediaUrl(ka.image), alt: ka.image?.alternativeText } : null,
        iconName: ka.icon?.name ?? "",
      };
    }),

    horteTitle: attrs.horteTitle ?? "",
    horteSubtitle: attrs.horteSubtitle ?? "",
    horte: horteList.map((h) => {
      const ha = h.attributes || h;
      return {
        id: h.id,
        name: ha.name ?? "",
        // Strapi-Feld heißt "adress" (Typo im Content-Type) — address nur als Fallback
        address: ha.adress ?? ha.address ?? "",
        distance: ha.distance ?? "",
        lat: ha.lat ?? null,
        lng: ha.lng ?? null,
      };
    }),
  };
}

/** --- Einblicke (collection type) -------------------------------------------
 * "category" is a single relation (manyToOne) to einblick-category. A post
 * has exactly one category — so a post tagged "Begabungsförderung" (used to
 * filter it into the Projekte & Erfolge page) will NOT also show up under a
 * different category on the main Blog page.
 */
const EINBLICK_UID = "einblicks";
const EINBLICK_CAT_UID = "einblick-categories";

export async function getEinblickeCategories() {
  const url = `${API}/api/${EINBLICK_CAT_UID}?sort=name:asc`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();
  return (json?.data || []).map((item) => {
    const a = item.attributes || item;
    return {
      id: item.id,
      name: a.name ?? "",
      description: a.description ?? "",
    };
  });
}

/**
 * @param {string} [categoryName] - optional, filters posts to only those
 *   whose category matches this name exactly.
 */
export async function getEinblicke(categoryName) {
  let url =
    `${API}/api/${EINBLICK_UID}` +
    `?populate=*&sort=date:desc&pagination[pageSize]=100`;

  if (categoryName) {
    url += `&filters[category][name][$eq]=${encodeURIComponent(categoryName)}`;
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  return (json?.data || []).map((item) => {
    const a = item.attributes || item;
    const cat = a.category?.data?.attributes || a.category;

    // Format date as German locale string
    let dateStr = "";
    if (a.date) {
      try {
        dateStr = new Date(a.date).toLocaleDateString("de-AT", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      } catch {
        dateStr = a.date;
      }
    }

    return {
      id: item.id,
      documentId: item.documentId ?? a.documentId ?? String(item.id),
      title: a.title ?? "",
      excerpt: a.excerpt ?? "",
      date: dateStr,
      category: cat?.name ?? "",
      image: a.cover ? mediaUrl(a.cover) : null,
      content: a.content ?? [],
      gallery: Array.isArray(a.gallery)
        ? a.gallery.map((g) => mediaUrl(g)).filter(Boolean)
        : (a.gallery?.data || []).map((g) => mediaUrl(g.attributes || g)).filter(Boolean),
    };
  });
}

/** --- Termine (collection + single type) ------------------------------------ */
const TERMINE_UID = "termins";
const TERMIN_CAT_UID = "termin-categories";
const TERMINE_PAGE_UID = "termine-page";

export async function getTerminePage() {
  const url = `${API}/api/${TERMINE_PAGE_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const attrs = json?.data?.attributes || json?.data || {};
  return {
    heroTitle: attrs.heroTitle ?? "Termine",
    heroSubtitle: attrs.heroSubtitle ?? "",
  };
}

export async function getTermineCategories() {
  const url = `${API}/api/${TERMIN_CAT_UID}?sort=name:asc`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  return (json?.data || []).map((item) => ({
    id: item.id,
    name: item.name ?? item.attributes?.name ?? "",
    color: item.color ?? item.attributes?.color ?? "#925a64",
  }));
}

/**
 * Liest die Startzeit aus dem freien Textfeld "time" und gibt sie in
 * Minuten seit Mitternacht zurück. Versteht u. a.:
 *   "9:00", "09:00", "14.30", "14-14.50 Uhr", "ab 18 Uhr", "8:00 – 12:00"
 * Es zählt immer die ERSTE Uhrzeit im Text (= Beginn).
 * Ohne erkennbare Uhrzeit (leer, "ganztägig" …) → -1, damit
 * ganztägige Termine am jeweiligen Tag zuerst stehen.
 */
function timeToMinutes(time) {
  const match = String(time || "").match(/(\d{1,2})(?:[:.](\d{2}))?/);
  if (!match) return -1;
  const hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  if (hours > 23 || minutes > 59) return -1;
  return hours * 60 + minutes;
}

/** Sortiert nach Datum, bei gleichem Tag nach Startzeit. */
function compareTermine(a, b) {
  const byDate = String(a.date).localeCompare(String(b.date));
  if (byDate !== 0) return byDate;
  return timeToMinutes(a.time) - timeToMinutes(b.time);
}

export async function getTermine() {
  const url = `${API}/api/${TERMINE_UID}?populate=*&sort=date:asc&pagination[pageSize]=200`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();

  const termine = (json?.data || []).map((item) => {
    const a = item.attributes || item;
    const cat = a.category?.data?.attributes || a.category;
    return {
      id: item.id,
      title: a.title ?? "",
      date: a.date ?? "",
      time: a.time ?? "",
      location: a.location ?? "",
      description: a.description ?? "",
      category: cat?.name ?? "",
    };
  });

  // Strapi sortiert nur nach Datum — die Uhrzeit ist ein Textfeld und
  // ließe sich dort nicht korrekt sortieren ("9:00" käme nach "14:00").
  // Deshalb hier: Datum, dann Startzeit.
  return termine.sort(compareTermine);
}

/** --- Mitgestalten page (single type) --------------------------------------- */
const MITGESTALTEN_UID = "mitgestalten-page";

export async function getMitgestaltenPage() {
  const url =
    `${API}/api/${MITGESTALTEN_UID}` +
    `?populate[heroImage]=true&populate[cards][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Mitgestalten page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImage: attrs.heroImage
      ? { url: mediaUrl(attrs.heroImage), alt: attrs.heroImage?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
  
    introText1: attrs.introText1 ?? "",
    introText2: attrs.introText2 ?? "",
    noteSectionTitle: attrs.noteSectionTitle ?? "",
    noteSectionText: attrs.noteSectionText ?? "",
    carouselTitle: attrs.carouselTitle ?? "",
    carouselKicker: attrs.carouselKicker ?? "",
    carouselCategory: attrs.carouselCategory ?? "Eltern",
    cards: (attrs.cards || []).map((c) => ({
      id: c.id,
      iconName: c.icon?.name ?? "star",
      title: c.title ?? "",
      text: c.text ?? "",
    })),
  };
}

/** --- Elternverein page (single type) --------------------------------------- */
const ELTERNVEREIN_UID = "elternverein-page";

export async function getElternvereinPage() {
  const url =
    `${API}/api/${ELTERNVEREIN_UID}` +
    `?populate[boardPhoto]=true` +
    `&populate[howFacts]=*` +
    `&populate[bankFacts]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Elternverein page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    boardPhoto: attrs.boardPhoto
      ? { url: mediaUrl(attrs.boardPhoto), alt: attrs.boardPhoto?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText1: attrs.introText1 ?? "",
    introText2: attrs.introText2 ?? "",
    membershipBadgeLabel: attrs.membershipBadgeLabel ?? "",
    membershipBadgeValue: attrs.membershipBadgeValue ?? "",
    benefitsTitle: attrs.benefitsTitle ?? "",
    benefitsSubtitle: attrs.benefitsSubtitle ?? "",
    benefits: typeof attrs.benefits === "string"
      ? attrs.benefits.split("\n").map((b) => b.trim()).filter(Boolean)
      : [],
    howTitle: attrs.howTitle ?? "",
    howText1: attrs.howText1 ?? "",
    howText2: attrs.howText2 ?? "",
    howText3: attrs.howText3 ?? "",
    howFacts: (attrs.howFacts || []).map((f) => ({ label: f.label ?? "", value: f.value ?? "" })),
    contactTitle: attrs.contactTitle ?? "",
    contactText: attrs.contactText ?? "",
    contactEmail: attrs.contactEmail ?? "",
    contactExternalUrl: attrs.contactExternalUrl ?? "",
    contactExternalLabel: attrs.contactExternalLabel ?? "",
    bankFacts: (attrs.bankFacts || []).map((f) => ({ label: f.label ?? "", value: f.value ?? "" })),
  };
}

/** --- Downloads & Formulare page (single type + downloads collection) -----
 * APPEND to bottom of lib/api.js
 *
 * Hinweis: Der Strapi-Single-Type heißt aktuell noch "wissenswert-page"
 * (Namensrest aus einer Kopiervorlage) und wird ausschließlich von dieser
 * Seite genutzt. Funktioniert so, aber falls ihr in Strapi mal aufräumen
 * wollt: Single-Type in z.B. "downloads-page" umbenennen und WISSENSWERT_UID
 * hier anpassen.
 */
const WISSENSWERT_UID = "wissenswert-page";
const DOWNLOAD_UID = "downloads";
const DOWNLOAD_CAT_UID = "download-categories";

export async function getWissenswertPage() {
  const url = `${API}/api/${WISSENSWERT_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const attrs = json?.data?.attributes || json?.data || {};
  return {
    heroTitle: attrs.heroTitle ?? "Downloads & Formulare",
    heroSubtitle: attrs.heroSubtitle ?? "",
    // Filter-Leiste
    allCategoriesLabel: attrs.allCategoriesLabel ?? "Alle",
    emptyStateText: attrs.emptyStateText ?? "Keine Einträge in dieser Kategorie.",
    pendingFileText: attrs.pendingFileText ?? "Datei wird vorbereitet…",
    // Sidebar: "Besonders relevant"-Karte
    sideCardKicker: attrs.sideCardKicker ?? "Wichtige Konzepte",
    sideCardTitle: attrs.sideCardTitle ?? "Besonders relevant",
    // Sidebar: Kontakt-Mini-Karte
    contactTitle: attrs.contactTitle ?? "Fragen zu Unterlagen?",
    contactText: attrs.contactText ?? "Bei Fragen zu Formularen oder Dokumenten schreiben Sie uns gerne direkt.",
    contactEmail: attrs.contactEmail ?? "",
    contactButtonLabel: attrs.contactButtonLabel ?? "Mail schreiben",
  };
}

// Sortiert nach dem manuell gepflegten "order"-Feld (kleinste Zahl zuerst).
// Einträge ohne Wert (order: null) fallen ans Ende und werden alphabetisch
// sortiert. Läuft komplett im Frontend, damit es nie an einem fehlenden
// "order"-Feld in Strapi scheitert (das würde die API sonst mit einem
// 400 "Invalid key order" quittieren, wenn man es im sort-Query anfragt).
function sortByOrder(list, fallbackKey) {
  return [...list].sort((a, b) => {
    const orderA = typeof a.order === "number" ? a.order : Infinity;
    const orderB = typeof b.order === "number" ? b.order : Infinity;
    if (orderA !== orderB) return orderA - orderB;
    return String(a[fallbackKey] || "").localeCompare(String(b[fallbackKey] || ""), "de");
  });
}

export async function getDownloadCategories() {
  const url = `${API}/api/${DOWNLOAD_CAT_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const categories = (json?.data || []).map((item) => {
    const a = item.attributes || item;
    return {
      id: item.id,
      name: a.name ?? "",
      order: typeof a.order === "number" ? a.order : null,
    };
  });
  return sortByOrder(categories, "name");
}

export async function getDownloads() {
  const url = `${API}/api/${DOWNLOAD_UID}?populate=*&pagination[pageSize]=100`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();

  const downloads = (json?.data || []).map((item) => {
    const a = item.attributes || item;
    const cat = a.category?.data?.attributes || a.category;
    const file = a.file?.data?.attributes || a.file;

    return {
      id: item.id,
      title: a.title ?? "",
      description: a.description ?? "",
      featured: a.featured ?? false,
      category: cat?.name ?? "",
      order: typeof a.order === "number" ? a.order : null,
      href: file?.url ? (file.url.startsWith("http") ? file.url : `${API}${file.url}`) : "#",
      fileSize: file?.size ? `${(file.size / 1024).toFixed(1)} MB` : "",
    };
  });
  return sortByOrder(downloads, "title");
}

/** --- FAQ page (single type) + FAQs (collection) ---------------------------- */
const FAQ_PAGE_UID = "faq-page";
const FAQ_UID = "faqs";
const FAQ_CAT_UID = "faq-categories";

export async function getFaqPage() {
  const url = `${API}/api/${FAQ_PAGE_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const attrs = json?.data?.attributes || json?.data || {};
  return {
    heroTitle: attrs.heroTitle ?? "Wissenswert",
    heroSubtitle: attrs.heroSubtitle ?? "",
    sideTitle: attrs.sideTitle ?? "",
    sideText: attrs.sideText ?? "",
    contactEmail: attrs.contactEmail ?? "",
  };
}

export async function getFaqCategories() {
  const url = `${API}/api/${FAQ_CAT_UID}?sort=name:asc`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  return (json?.data || []).map((item) => ({
    id: item.id,
    name: item.name ?? item.attributes?.name ?? "",
  }));
}

export async function getFaqs() {
const url = `${API}/api/${FAQ_UID}?populate=*&sort=order:asc&pagination[pageSize]=100`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  return (json?.data || []).map((item) => {
    const a = item.attributes || item;
    const cat = a.category?.data?.attributes || a.category;
    return {
      id: item.id,
      question: a.question ?? "",
      answer: a.answer ?? "",
      category: cat?.name ?? "",
    };
  });
}

/** --- Begabungsförderung: Konzept & Ziele (single type) --------------------
 * ⚠️ Matches the current Strapi API ID, which has a typo ("bagabung" instead
 * of "begabung"). If you rename the content type in Strapi, change this back
 * to "begabung-konzept-page".
 */

const BERATUNG_UID = "beratung-page";

export async function getBeratungPage() {
  const url =
    `${API}/api/${BERATUNG_UID}` +
    `?populate[heroImage]=true` +
    `&populate[cards][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Beratung page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImage: attrs.heroImage
      ? { url: mediaUrl(attrs.heroImage), alt: attrs.heroImage?.alternativeText }
      : null,
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
    cards: (attrs.cards || []).map((c) => ({
      id: c.id,
      iconName: c.icon?.name ?? "star",
      title: c.title ?? "",
      text: c.text ?? "",
      email: c.email ?? "",
      website: c.website ?? "",
    })),
  };
}

const BEGABUNG_KONZEPT_UID = "bagabung-konzept-page";

export async function getBegabungKonzeptPage() {
  const url =
    `${API}/api/${BEGABUNG_KONZEPT_UID}` +
    `?populate[heroImages]=true` +
    `&populate[cards][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Begabung Konzept page is empty or unpublished.");
  const attrs = data.attributes || data;

  const heroImagesRaw = Array.isArray(attrs.heroImages)
    ? attrs.heroImages
    : attrs.heroImages?.data || [];

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",
    heroImages: heroImagesRaw
      .map((img) => {
        const a = img.attributes || img;
        return { url: mediaUrl(a), alt: a.alternativeText };
      })
      .filter((img) => img.url),
    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    sectionTitle: attrs.sectionTitle ?? "",
    sectionSubtitle: attrs.sectionSubtitle ?? "",
    cards: (attrs.cards || []).map((c) => ({
      id: c.id,
      iconName: c.icon?.name ?? c.icon ?? "star",
      title: c.title ?? "",
      text: c.text ?? "",
    })),
  };
}

/** --- Begabungsförderung – Angebote & Kurse (single type) ------------------- */
const BEGABUNG_UID = "begabung-angebote-page";

export async function getBegabungAngebotePage() {
  const url =
    `${API}/api/${BEGABUNG_UID}` +
    `?populate[introImage]=true` +
    `&populate[vormittagskurse][populate]=*` +
    `&populate[nachmittagskurse][populate]=*`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();

  const data = json?.data;
  if (!data) throw new Error("Begabung-Angebote page is empty or unpublished.");
  const attrs = data.attributes || data;

  return {
    heroTitle: attrs.heroTitle ?? "",
    heroSubtitle: attrs.heroSubtitle ?? "",

    introKicker: attrs.introKicker ?? "",
    introTitle: attrs.introTitle ?? "",
    introText: attrs.introText ?? "",
    introImage: attrs.introImage
      ? { url: mediaUrl(attrs.introImage), alt: attrs.introImage?.alternativeText }
      : null,

    vormittagTitle: attrs.vormittagTitle ?? "",
    vormittagSubtitle: attrs.vormittagSubtitle ?? "",
    vormittagskurse: (attrs.vormittagskurse || []).map((k) => ({
      id: k.id,
      title: k.title ?? "",
      text: k.text ?? "",
      image: k.image ? { url: mediaUrl(k.image), alt: k.image?.alternativeText } : null,
      iconName: k.icon?.name ?? "",
    })),

    nachmittagTitle: attrs.nachmittagTitle ?? "",
    nachmittagSubtitle: attrs.nachmittagSubtitle ?? "",
    nachmittagskurse: (attrs.nachmittagskurse || []).map((k) => ({
      id: k.id,
      title: k.title ?? "",
      text: k.text ?? "",
      image: k.image ? { url: mediaUrl(k.image), alt: k.image?.alternativeText } : null,
      iconName: k.icon?.name ?? "",
    })),
  };
}
/** --- Begabungsförderung → Projekte & Erfolge (single type) ----------------
 * The intro text comes from its own single type, but the actual project
 * list is just the Einblicke (Blog) collection filtered to the
 * "Begabungsförderung" category — see getEinblicke(BEGABUNG_PROJEKTE_CATEGORY)
 * above and BegabungProjektePage.jsx.
 */
const BEGABUNG_PROJEKTE_PAGE_UID = "begabung-projekte-page";
export const BEGABUNG_PROJEKTE_CATEGORY = "Begabungsförderung";

export async function getBegabungProjektePage() {
  const url = `${API}/api/${BEGABUNG_PROJEKTE_PAGE_UID}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status} – ${await res.text()}`);
  const json = await res.json();
  const attrs = json?.data?.attributes || json?.data || {};
  return {
    heroTitle: attrs.heroTitle ?? "Projekte & Erfolge",
    heroSubtitle: attrs.heroSubtitle ?? "",
  };
}



/** --- Menu routes (MenuItem collection) -------------------------------------
 * Each published MenuItem with a `slug` and a `page` (enumeration) becomes a
 * route: "/<slug>" → the page component registered under that key in App.jsx.
 * Folder items (no page) are skipped.
 */
/** --- Menu routes (MenuItem collection) -------------------------------------
 * Each published MenuItem with a `slug` and a `page` (enumeration) becomes a
 * route: "/<slug>" → the page component registered under that key in
 * App.jsx (the PAGES object).
 *
 * Dropdown parents (e.g. "Aktuelles") have page "none" or no page, so they
 * never become a route — App.jsx ignores keys it doesn't know.
 */
export async function getMenuRoutes() {
  const url =
    `${API}/api/menu-items` +
    `?fields[0]=slug&fields[1]=page` +
    `&pagination[pageSize]=200`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();

  return (json?.data || [])
    .map((item) => item.attributes || item)
    .filter((a) => a.slug && a.page && a.page !== "none")
    .map((a) => ({
      path: "/" + String(a.slug).trim().replace(/^\/+|\/+$/g, ""),
      page: a.page,
    }));
}